// popup.util.ts
export type PopupResult =
  | { status: 'opened'; win: Window }
  | { status: 'blocked' }
  | { status: 'failed'; error?: unknown };

  export interface PopupOptions {
    timeoutMs?: number;          // default 4000
    debug?: boolean;             // console.debug logs
    trampolinePath?: string;     // default "/assets/popup-redirect.html"
    useOpenerHandshake?: boolean; // default true when in iframe
  }
  
  function randId() {
    if ('randomUUID' in crypto) return (crypto as any).randomUUID();
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
  const d = (on: boolean, ...args: any[]) => on && console.debug('[popup]', ...args);
  
  function inIframe(): boolean {
    try { return window.parent !== window; } catch { return true; }
  }
  
  function log(debug: boolean, ...args: any[]) {
    if (debug) console.debug('[popup]', ...args);
  }
  
  function sameOrigin(a: string, b: string): boolean {
    try { return new URL(a).origin === new URL(b).origin; } catch { return false; }
  }

export function openTabSafely(url: string): PopupResult {
  // Must be called synchronously in a user gesture
  const win = window.open(url, '_blank', 'noopener');

  console.log('win', win);

  // If blocked, many browsers return null/undefined
  if (!win || win.closed || typeof win.closed === 'undefined') {
    return { status: 'blocked' };
  }

  try {
    // Extra safety: detach opener and navigate
    (win as any).opener = null;
    win.location.assign(url);
    return { status: 'opened', win };
  } catch (error) {
    // Some engines may open a blank tab but refuse programmatic navigation
    try {
      // Fallback: render a clickable link in the new tab
      win.document.write(
        `<p>Click <a href="${url}" rel="noopener" target="_self">here</a> if not redirected.</p>`
      );
      win.document.close();
      return { status: 'opened', win };
    } catch (error2) {
      return { status: 'failed', error: error2 ?? error };
    }
  }
}

export type PopupAck = { ok: true } | { ok: false; reason?: string };

export async function openTabWithAck(finalUrl: string, opts: PopupOptions = {}): Promise<boolean> {
  const debug = !!opts.debug;
  const timeoutMs = opts.timeoutMs ?? 4000;
  const trampolinePath = opts.trampolinePath ?? '/popup-redirect.html';

  // In an embed, prefer opener-based handshake (BC/storage may be partitioned)
  const useOpenerHandshake = opts.useOpenerHandshake ?? inIframe();

  const channelId = 'popup-' + randId();
  const openerOrigin = location.origin;

  // Build trampoline URL with params the new tab will use to ack us
  const tramp = new URL(trampolinePath, openerOrigin);
  tramp.searchParams.set('ch', channelId);
  tramp.searchParams.set('dbg', debug ? '1' : '0');
  tramp.searchParams.set('opener', openerOrigin); // for secure targetOrigin in postMessage
  tramp.hash = encodeURIComponent(finalUrl);

  d(debug, 'env', {
    ua: navigator.userAgent,
    inIframe: inIframe(),
    openerOrigin,
    trampolineUrl: tramp.toString(),
    finalUrl,
    useOpenerHandshake,
  });

  // Listeners
  let resolved = false;
  let timer: number | undefined;
  const cleanup: Array<() => void> = [];

  const done = (ok: boolean, why?: string) => {
    if (resolved) return;
    resolved = true;
    if (timer) clearTimeout(timer);
    cleanup.forEach(fn => { try { fn(); } catch {} });
    d(debug, 'result', { ok, why });
    return ok;
  };

  // message event (for opener handshake)
  const onMessage = (ev: MessageEvent) => {
    try {
      const data = ev?.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'POPUP_ACK' && data.ch === channelId && data.opened === true) {
        d(debug, 'postMessage ACK', data);
        done(true, 'opener-postMessage');
      }
    } catch {}
  };
  window.addEventListener('message', onMessage);
  cleanup.push(() => window.removeEventListener('message', onMessage));

  // (Optional) keep the BC/storage listeners in case you ever run same-site top-level
  let bc: BroadcastChannel | null = null;
  try {
    if ('BroadcastChannel' in window) {
      bc = new BroadcastChannel(channelId);
      bc.onmessage = (ev) => {
        d(debug, 'BroadcastChannel message', ev?.data);
        if (ev?.data?.opened) done(true, 'bc');
      };
      cleanup.push(() => bc && bc.close());
      d(debug, 'BroadcastChannel supported: YES');
    } else {
      d(debug, 'BroadcastChannel supported: NO');
    }
  } catch (e) {
    d(debug, 'BroadcastChannel error', e);
  }
  const onStorage = (e: StorageEvent) => {
    if (e.key === 'popup:' + channelId) {
      d(debug, 'storage event received', e.newValue);
      done(true, 'storage');
    }
  };
  window.addEventListener('storage', onStorage);
  cleanup.push(() => window.removeEventListener('storage', onStorage));

  // Timeout
  timer = window.setTimeout(() => {
    d(debug, 'timeout', timeoutMs);
    done(false, 'timeout');
  }, timeoutMs);

  // IMPORTANT: remove 'noopener' when you want opener handshake
  const features = useOpenerHandshake ? '' : 'noopener';

  try {
    const win = window.open(tramp.toString(), '_blank', features);
    d(debug, 'window.open returned', win);
  } catch (e) {
    d(debug, 'window.open threw', e);
  }

  // Return promise resolves via listeners/timeout
  return new Promise<boolean>(res => {
    const doneWrapper = (ok: boolean) => res(ok);
    // piggyback on our existing done() through the listeners
    const check = () => {
      if (resolved) res(true);
      else setTimeout(check, 10);
    };
    setTimeout(check, 10);
  });
}
